import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import moment, { Moment } from 'moment';
import { DatePicker, Menu, Popover } from 'antd';
import classNames from 'classnames';
import { nonNull } from '@helpers/non-null';
import { timeList } from '@components/DatePickerComponent/timeList';
import { CalendarDueTimeIcon, CrossIcon } from '@icons';
import './InlineDatePicker.less';

export interface InlineDatePickerProps {
  value?: Moment | null;
  onChange?: (value: Moment | null) => void;
  clearable?: boolean;
  showTime?: boolean;
  defaultTime?: Moment;
  selectedDefaultTime?: Moment;
  timeText?: string;
  selectTimeText?: string;
  disabledDate?: (date: Moment) => boolean;
  disabledTime?: (date: Moment) => boolean;
  clearableTime?: boolean;
}

const endOfDayTime = moment().endOf('day');

const ITEM_HEIGHT = 40;
const VISIBLE_ITEMS = 4;

export default function InlineDatePicker(props: InlineDatePickerProps) {
  const {
    onChange,
    clearable,
    showTime,
    timeText,
    selectTimeText,
    selectedDefaultTime,
    disabledDate,
    disabledTime,
    clearableTime = true
  } = props;
  const container = useRef<HTMLDivElement>(null);
  const menuTimeWrapperRef = useRef<HTMLDivElement | null>(null);
  const [value, setValue] = useState(props.value);
  const [time, setTime] = useState(props.value);
  if (
    props.value !== undefined &&
    props.value?.valueOf() !== value?.valueOf()
  ) {
    setValue(props.value);
    setTime(props.value);
  }

  const defaultTime = props.defaultTime ?? endOfDayTime;
  const nonDefaultTimeSelected =
    time &&
    (time.hours() !== defaultTime.hours() ||
      time.minutes() !== defaultTime.minutes());

  const selectedTimeKey = nonDefaultTimeSelected
    ? timeList.find(
        (x) => x.hours === time.hours() && x.minutes === time.minutes()
      )?.id
    : undefined;

  const [timeMenuVisible, setTimeMenuVisible] = useState(false);
  const items = useMemo(
    () =>
      timeList
        .filter((x) => {
          if (!disabledTime) return true;
          if (x.id === selectedTimeKey) return true;
          const time = moment(value ?? moment());
          time.endOf('minute');
          time.hours(x.hours);
          time.minutes(x.minutes);
          return !disabledTime(time);
        })
        .map((x) => {
          return {
            key: x.id,
            label: x.time,
            'data-time-id': x.id
          };
        }),
    [selectedTimeKey, disabledTime]
  );

  const nextItems = useMemo(
    () =>
      items.slice(0, VISIBLE_ITEMS).map((item) => ({
        label: item.label,
        key: `${item.key}-next`
      })),
    [items]
  );
  const prevItems = useMemo(
    () =>
      items.slice(items.length - VISIBLE_ITEMS).map((item) => ({
        label: item.label,
        key: `${item.key}-prev`
      })),
    [items]
  );

  const [mounted, setMounted] = useState(false);
  useLayoutEffect(() => {
    setMounted(true);
    const onClickOutside = () => {
      setTimeMenuVisible(false);
    };
    document.addEventListener('click', onClickOutside);
    return () => {
      document.removeEventListener('click', onClickOutside);
      setMounted(false);
    };
  }, []);
  const hasTimeToSelect = useMemo(() => {
    const allowedTimes = timeList.filter((x) => {
      if (!disabledTime) return true;
      const time = moment(value ?? moment());
      time.endOf('minute');
      time.hours(x.hours);
      time.minutes(x.minutes);
      return !disabledTime(time);
    });
    return allowedTimes.length;
  }, [disabledTime, value]);

  const handleScroll = (e: any) => {
    const numItems = items?.length || 1;
    const totalItems = numItems + VISIBLE_ITEMS * 2;
    if (e.target.scrollTop >= (totalItems - VISIBLE_ITEMS) * ITEM_HEIGHT) {
      e.target.scrollTop = VISIBLE_ITEMS * ITEM_HEIGHT;
    } else if (e.target.scrollTop === 0) {
      e.target.scrollTop = numItems * ITEM_HEIGHT;
    }
  };

  useEffect(() => {
    if (!timeMenuVisible) return;
    const timeoutId = setTimeout(() => {
      const menuTimeWrapper = menuTimeWrapperRef.current;
      if (!menuTimeWrapper) return;
      const selectedItem = menuTimeWrapper.querySelector(
        `li[data-time-id="${selectedTimeKey}"]`
      ) as HTMLElement | null;
      const defaultItem = menuTimeWrapper.querySelector(
        `li[data-time-id="33"]`
      ) as HTMLElement | null; // 8:00 am
      if (selectedItem) {
        menuTimeWrapper.scrollTo({
          top: selectedItem.offsetTop - menuTimeWrapper.offsetTop,
          behavior: 'auto'
        });
        return;
      }
      if (defaultItem) {
        menuTimeWrapper.scrollTo({
          top: defaultItem.offsetTop - menuTimeWrapper.offsetTop,
          behavior: 'auto'
        });
      }
    }, 300);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [timeMenuVisible, selectedTimeKey]);

  return (
    <div
      className="inline-date-picker"
      ref={container}
      onClick={(e) => {
        e.stopPropagation();
        setTimeMenuVisible(false);
        const target = e.target as any;
        if (
          clearable &&
          target.closest &&
          target.closest('.ant-picker-cell-selected')
        ) {
          setValue(null);
          if (onChange) onChange(null);
        }
      }}
    >
      <DatePicker
        open={mounted}
        onOpenChange={undefined}
        getPopupContainer={() => nonNull(container.current)}
        value={value}
        disabledDate={(date) => {
          if (!disabledDate) return false;
          if (value && date.isSame(value, 'date')) return false;
          date.endOf('minute');
          date.hours(defaultTime.hours());
          date.minutes(defaultTime.minutes());
          return disabledDate(date);
        }}
        onChange={(v) => {
          // if past current time, set default time to next hour or 23:59
          if (selectedDefaultTime && v) {
            const now = moment().local();
            const selectedTimeLocal = selectedDefaultTime.local();
            const isToday = selectedTimeLocal.isSame(v.local(), 'day');
            const nowHours = now.hours();
            const defaultHours = selectedTimeLocal.hours();
            if (isToday && defaultHours <= nowHours) {
              if (nowHours >= 23) {
                selectedTimeLocal.set({
                  hour: 23,
                  minute: 59,
                  second: 59,
                  millisecond: 999
                });
              } else {
                selectedTimeLocal.set({
                  hour: nowHours + 1,
                  minute: 0,
                  second: 0,
                  millisecond: 0
                });
              }
            }
          }

          const _defaultTime = selectedDefaultTime || defaultTime;
          if (v && selectedDefaultTime) {
            v.endOf('minute');
            v.hours((time ?? _defaultTime).hours());
            v.minutes((time ?? _defaultTime).minutes());
          }
          setValue(v);
          if (selectedDefaultTime) setTime(time || _defaultTime);
          if (onChange) onChange(v);
        }}
        renderExtraFooter={() => {
          if (!showTime) return null;
          return (
            <div className="inline-date-picker__footer">
              <div
                className={classNames({
                  'inline-date-picker__select-time-button': true,
                  'inline-date-picker__select-time-button--disabled':
                    !hasTimeToSelect,
                  'inline-date-picker__select-time-button--active':
                    nonDefaultTimeSelected || timeMenuVisible
                })}
                onClick={(e) => {
                  e.stopPropagation();
                  setTimeMenuVisible(!timeMenuVisible);
                }}
              >
                <CalendarDueTimeIcon />
                <span>
                  {nonDefaultTimeSelected || timeMenuVisible
                    ? timeText ?? 'Time'
                    : selectTimeText ?? 'Select time'}
                </span>
              </div>
              {(nonDefaultTimeSelected || timeMenuVisible) && (
                <Popover
                  open={timeMenuVisible}
                  getPopupContainer={(el) =>
                    nonNull(el.closest('.ant-picker-footer'))
                  }
                  overlayClassName="time-popover"
                  content={
                    <div
                      ref={menuTimeWrapperRef}
                      style={{
                        height: ITEM_HEIGHT * VISIBLE_ITEMS
                      }}
                      onScroll={handleScroll}
                      className="inline-date-picker__time-menu-wrapper"
                    >
                      <Menu
                        className="inline-date-picker__time-menu"
                        selectedKeys={
                          selectedTimeKey
                            ? [
                                selectedTimeKey,
                                `${selectedTimeKey}-prev`,
                                `${selectedTimeKey}-next`
                              ]
                            : []
                        }
                        onSelect={({ key }: { key: string }) => {
                          const _key = key.replace(/-prev|-next/g, '');
                          setTimeMenuVisible(false);
                          const time = nonNull(
                            timeList.find((x) => x.id === _key)
                          );
                          const v = value ? moment(value) : moment();
                          v.endOf('minute');
                          v.hours(time.hours);
                          v.minutes(time.minutes);
                          setTime(v);
                          if (value) {
                            setValue(v);
                            if (onChange) onChange(v);
                          }
                        }}
                        onDeselect={() => {
                          if (!clearableTime) return;
                          setTimeMenuVisible(false);
                          const v = value ? moment(value) : moment();
                          v.endOf('minute');
                          v.hours(defaultTime.hours());
                          v.minutes(defaultTime.minutes());
                          setTime(v);
                          if (value) {
                            setValue(v);
                            if (onChange) onChange(v);
                          }
                        }}
                        items={[...prevItems, ...items, ...nextItems]}
                      />
                    </div>
                  }
                  placement="rightBottom"
                  trigger={[]}
                >
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      setTimeMenuVisible(!timeMenuVisible);
                    }}
                    className={classNames({
                      'inline-date-picker__time': !!time
                    })}
                  >
                    {time?.format('LT') ?? null}
                    {clearableTime && nonDefaultTimeSelected && (
                      <CrossIcon
                        onClick={(e: any) => {
                          e.stopPropagation();
                          const v = value ? moment(value) : moment();
                          v.endOf('minute');
                          v.hours(defaultTime.hours());
                          v.minutes(defaultTime.minutes());
                          setTime(v);
                          if (value) {
                            setValue(v);
                            if (onChange) onChange(v);
                          }
                        }}
                      />
                    )}
                  </div>
                </Popover>
              )}
            </div>
          );
        }}
      />
    </div>
  );
}
